import { DeviceItem, useLocaleStore } from '@/stores'
import { rgbToHex } from '@/utils/color'
import {
  BaseCurrentLightEffect,
  BaseLightEffect,
  BaseScreenCapability,
  hasLightingEditableField,
  LIGHTING_EDITABLE_FIELD,
  LIGHTING_TARGET,
  LightingEffectDefinition,
} from '@rapoo/device-sdk'
import { computed, ref } from 'vue'

export function useScreenLogo(itemRef: { value: DeviceItem }) {
  const { t } = useLocaleStore()
  const device = computed(() => itemRef.value.original)

  const effect = ref<BaseCurrentLightEffect | null>(null)
  const initCurrentLightEffect = async () => {
    const lighting = device.value.features.baseLighting
    if (lighting) {
      effect.value = await lighting.getCurrentEffect(LIGHTING_TARGET.BaseLightScreen)
    }
  }
  const screenLogoCapability = computed(() =>
    device.value.manifest.capabilities.find(
      (c): c is BaseScreenCapability => c.type === 'baseScreen' && c.config.screenType === 'logo',
    ),
  )

  const lightEffects = ref<LightingEffectDefinition[]>([])
  const initLightEffects = async () => {
    const lighting = device.value.features.baseLighting
    if (lighting) {
      lightEffects.value = await lighting.getEffects(LIGHTING_TARGET.BaseLightScreen)
    }
  }

  const currentEffectDefinition = computed(() => {
    if (!effect.value) return null
    return lightEffects.value.find((item) => item.id === effect.value?.modeId) ?? null
  })

  const effectName = computed(() => currentEffectDefinition.value?.i18nKey ?? null)

  const canEditColor = computed(() => {
    return hasLightingEditableField(currentEffectDefinition.value, LIGHTING_EDITABLE_FIELD.Color)
  })

  const canEditSpeed = computed(() => {
    return hasLightingEditableField(currentEffectDefinition.value, LIGHTING_EDITABLE_FIELD.Speed)
  })

  const canEditBrightness = computed(() => {
    return hasLightingEditableField(
      currentEffectDefinition.value,
      LIGHTING_EDITABLE_FIELD.Brightness,
    )
  })

  const baseLight = ref<BaseLightEffect | null>(null)
  const initBaseLight = async (mode: number) => {
    const lighting = device.value.features.baseLighting
    if (lighting) {
      baseLight.value = await lighting.getLightEffect(mode, LIGHTING_TARGET.BaseLightScreen)
    }
  }

  const initScreenLogo = async () => {
    // ESPR5 等无 baseScreen(logo) 能力的底座跳过，避免误调 getCurrentEffect / getLightEffect
    if (!screenLogoCapability.value) return
    await Promise.all([initCurrentLightEffect(), initLightEffects()])
    if (effect.value) {
      const currentModeId = effect.value.modeId
      const matchedEffect = lightEffects.value.find((item) => item.id === currentModeId)
      if (!matchedEffect) return

      await initBaseLight(matchedEffect.id)
    }
  }

  const screenLogoData = computed(() => {
    if (device.value.manifest.type !== 'base') {
      return null
    }

    if (!screenLogoCapability.value) return null

    const data: { name: string; value: string }[] = []

    if (effectName.value) {
      let name = t(effectName.value)
      if (canEditColor.value && baseLight.value?.color) {
        const [r, g, b] = baseLight.value.color
        name += ` (${rgbToHex({ r, g, b })})`
      }
      data.push({
        name: t('home.overview.screenLogo.lightMode'),
        value: name,
      })
    }

    const chargeLightLabel =
      effect.value?.chargeLightDisplayMode === 'ambient'
        ? t('home.overview.screenLogo.ambientLight')
        : effect.value?.chargeLightDisplayMode === 'charging'
          ? t('home.overview.screenLogo.chargeLight')
          : null
    if (chargeLightLabel) {
      data.push({
        name: t('home.overview.screenLogo.chargeState'),
        value: chargeLightLabel,
      })
    }

    if (canEditSpeed.value && baseLight.value?.frequency !== undefined) {
      data.push({
        name: t('home.overview.screenLogo.speed'),
        value: `${baseLight.value.frequency}%`,
      })
    }

    if (canEditBrightness.value && baseLight.value?.brightness !== undefined) {
      data.push({
        name: t('home.overview.screenLogo.brightness'),
        value: `${baseLight.value.brightness}%`,
      })
    }

    if (!data.length) return null

    return {
      title: t('home.overview.screenLogo.title'),
      data,
    }
  })

  return {
    screenLogoData,
    initScreenLogo,
  }
}
